import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CustomSelect from "../../../utils/CustomSelect/CustomSelect";
import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";
import AlertPayment from "./AlertPayment";
import PurchasedAddons from "./PurchasedAddons";

export default function AddonsList({ userDetails, buttonColorScheme }) {
  const baseUrl = getHostDomain();
  const [status, setStatus] = useState("idle");
  const [addonsList, setAddonsList] = useState([]);
  const [currentUserAddons, setCurrentUserAddons] = useState([]);
  const { watch, setValue, register, handleSubmit, reset } = useForm({
    defaultValues: { lookup_key: "", qty: 1 },
  });

  useEffect(() => {
    (async () => {
      try {
        setStatus("loadingAddons");
        const activeAddonsResponse = await fetchController(
          baseUrl + "/api/v1/billing/get_active_addons"
        );
        setAddonsList(activeAddonsResponse.data);
        if (userDetails.stripe_has_payment_method) fetchCurrentUserAddons();
      } catch (error) {
        toast.error(error);
      } finally {
        setStatus("idle");
      }
    })();
  }, []);

  const fetchCurrentUserAddons = async () => {
    try {
      setStatus("loading");
      const userAddons = await fetchController(
        baseUrl + "/api/v1/billing/get_my_addons"
      );
      setCurrentUserAddons(userAddons);
    } catch (error) {
      console.log("Error fetching addons");
    } finally {
      setStatus("idle");
    }
  };

  const loading = status === "loadingAddons";
  const purchasingAddon = status === "purchasingAddon";
  const hasAddedPaymentMethod = Boolean(userDetails?.stripe_has_payment_method);

  const onBuyAddons = async (data) => {
    try {
      setStatus("purchasingAddon");
      await fetchController(
        baseUrl + "/api/v1/billing/buy_addon",
        "POST",
        data
      );
      toast.success("Purchased");
      reset({ qty: 1 });
      fetchCurrentUserAddons();
    } catch (_) {
      toast.error("Some error occured");
    } finally {
      setStatus("idle");
    }
  };

  const addonOptionMapper = (addon) => ({
    value: addon.plan_lookup_key,
    label: addon.name,
  });
  const selectedLookupKey = watch("lookup_key");
  const selectedAddon = addonsList
    .map(addonOptionMapper)
    .find((addon) => addon.value === selectedLookupKey);
  const canBuy = hasAddedPaymentMethod && selectedLookupKey && watch("qty");

  if (loading) {
    return (
      <Flex justify="center" align="center" py={6}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Stack spacing={3}>
      {currentUserAddons.length > 0 && (
        <>
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
              Purchased
            </Text>
            <PurchasedAddons
              addonsList={currentUserAddons}
              fetchCurrentUserAddons={fetchCurrentUserAddons}
            />
          </Box>
          <Divider />
        </>
      )}

      {!hasAddedPaymentMethod && <AlertPayment />}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onBuyAddons)();
        }}
      >
        <Stack spacing={3}>
          <Flex gap={2} align="flex-end" wrap={{ base: "wrap", sm: "nowrap" }}>
            <FormControl flex={2} minW="180px">
              <FormLabel fontSize="sm" mb={1}>
                Addon
              </FormLabel>
              <CustomSelect
                required
                value={selectedAddon?.value}
                {...register("lookup_key", { required: true })}
                onChange={({ value }) => setValue("lookup_key", value)}
                options={addonsList.map(addonOptionMapper)}
                isOptionDisabled={!hasAddedPaymentMethod}
              />
            </FormControl>

            <FormControl flex={1} minW="100px" maxW="140px">
              <FormLabel fontSize="sm" mb={1}>
                Qty
              </FormLabel>
              <NumberInput min={1} allowMouseWheel>
                <NumberInputField
                  {...register("qty", {
                    valueAsNumber: true,
                    required: true,
                    min: 1,
                  })}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <Button
              type="submit"
              isLoading={purchasingAddon}
              isDisabled={!canBuy}
              colorScheme={buttonColorScheme}
              minW="110px"
            >
              Buy addon
            </Button>
          </Flex>
        </Stack>
      </form>
    </Stack>
  );
}
